/**
 * Shared visual primitives for the warehouse detail drawers (orders, products).
 *
 * Small presentational building blocks used to lay out a drawer's summary
 * sections consistently — a section heading, a labeled metric, and a
 * dot-accented tag chip.
 *
 * Contains:
 * - SectionLabel — uppercase section heading, optional accent dot
 * - Metric — label + value pair, larger emphasis variant for headline stats
 * - TagChip — small outlined chip with a colored accent dot
 */

import { Box, Chip, Stack, Typography } from '@mui/material';
import React from 'react';

import { useUser } from '@sophocles/app';
import { formatNumber } from '@sophocles/utils/formatUtils';

export function SectionLabel({ children, accent }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      {accent && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: accent }} />}
      <Typography
        variant="body2"
        sx={{
          fontSize: '0.75rem',
          fontWeight: 600,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          color: 'text.secondary',
        }}
      >
        {children}
      </Typography>
    </Stack>
  );
}

export function Metric({ label, value, accent, emphasis }) {
  const { user } = useUser();
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: 'monospace',
          fontSize: emphasis ? '1.5rem' : '1.25rem',
          fontWeight: emphasis ? 700 : 500,
          lineHeight: 1.2,
          color: emphasis && accent ? accent : 'text.primary',
        }}
      >
        {typeof value === 'number' ? formatNumber(value, user) : value}
      </Typography>
    </Box>
  );
}

export function TagChip({ label, color }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      icon={<Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, ml: '6px !important' }} />}
      label={label}
      sx={{
        height: 22,
        borderRadius: 'var(--filter-chip-radius)',
        borderColor: 'var(--md-outline-variant)',
        bgcolor: 'transparent',
        '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem', fontWeight: 500, color: 'text.secondary' },
      }}
    />
  );
}
